import type { GlobalThemeOverrides } from 'naive-ui'

import { brand, status, light, dark, radii, typography } from './tokens'

// Базовые overrides, общие для светлой и тёмной темы.
// Зависят только от brand/status — те одинаковы.
function commonBase() {
  return {
    common: {
      primaryColor: brand.primary,
      primaryColorHover: brand.primaryHover,
      primaryColorPressed: brand.primaryPressed,
      primaryColorSuppl: brand.primary,
      successColor: status.success,
      successColorHover: status.success,
      successColorPressed: status.success,
      warningColor: status.warning,
      warningColorHover: status.warning,
      warningColorPressed: status.warning,
      errorColor: status.error,
      errorColorHover: status.error,
      errorColorPressed: status.error,
      infoColor: status.info,
      infoColorHover: status.info,
      infoColorPressed: status.info,
      borderRadius: `${radii.control}px`,
      borderRadiusSmall: `${radii.control}px`,
      fontFamily: typography.fontFamily,
      fontFamilyMono: typography.monoFamily,
      fontSize: `${typography.bodyFontSize}px`,
    },
  }
}

// Светлая тема.
export const lightOverrides: GlobalThemeOverrides = {
  ...commonBase(),
  common: {
    ...commonBase().common,
    bodyColor: light.bg,
    baseColor: light.surface,
    cardColor: light.surface,
    modalColor: light.surface,
    popoverColor: light.surface,
    tableColor: light.surface,
    tableHeaderColor: light.surface2,
    inputColor: light.surface,
    actionColor: light.surface2,
    hoverColor: light.surfaceHover,
    borderColor: light.border,
    dividerColor: light.border,
    textColorBase: light.text,
    textColor1: light.text,
    textColor2: light.text2,
    textColor3: light.text3,
    placeholderColor: light.text3,
    closeIconColor: light.text2,
    closeIconColorHover: light.text,
    iconColor: light.text2,
    iconColorHover: light.text,
    boxShadow1: '0 1px 2px rgba(16, 24, 40, 0.04)',
    boxShadow2: '0 8px 28px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06)',
    boxShadow3: '0 8px 28px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06)',
  },
  ...componentOverrides(light),
}

// Тёмная тема.
export const darkOverrides: GlobalThemeOverrides = {
  ...commonBase(),
  common: {
    ...commonBase().common,
    bodyColor: dark.bg,
    baseColor: dark.surface,
    cardColor: dark.surface,
    modalColor: dark.surface,
    popoverColor: dark.surface,
    tableColor: dark.surface,
    tableHeaderColor: dark.surface2,
    inputColor: dark.surface,
    actionColor: dark.surface2,
    hoverColor: dark.surfaceHover,
    borderColor: dark.border,
    dividerColor: dark.border,
    textColorBase: dark.text,
    textColor1: dark.text,
    textColor2: dark.text2,
    textColor3: dark.text3,
    placeholderColor: dark.text3,
    closeIconColor: dark.text2,
    closeIconColorHover: dark.text,
    iconColor: dark.text2,
    iconColorHover: dark.text,
    boxShadow1: 'none',
    boxShadow2: '0 10px 34px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.4)',
    boxShadow3: '0 10px 34px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.4)',
  },
  ...componentOverrides(dark),
}

// componentOverrides — переопределения для конкретных компонентов.
// Здесь сосредоточена «визуальная плотность»: высота меню, скругления тегов,
// шапка таблицы и т.д. Зависят от палитры темы.
function componentOverrides(p: typeof light | typeof dark): GlobalThemeOverrides {
  return {
    Layout: {
      headerColor: p.surface,
      headerBorderColor: p.border,
      siderColor: p.surface,
      siderBorderColor: p.border,
      color: p.bg,
    },
    Menu: {
      // Плотность пунктов из макета: 34px, скруглённый, размер шрифта 13.5.
      itemHeight: '34px',
      itemTextColor: p.text2,
      itemIconColor: p.text3,
      itemColorHover: p.surfaceHover,
      itemTextColorHover: p.text,
      itemIconColorHover: p.text2,
      itemColorActive: brand.primaryWeak,
      itemTextColorActive: brand.primary,
      itemIconColorActive: brand.primary,
      itemColorActiveHover: brand.primaryWeak,
      itemTextColorActiveHover: brand.primary,
      itemIconColorActiveHover: brand.primary,
      itemColorActiveCollapsed: brand.primaryWeak,
      borderRadius: `${radii.control}px`,
      fontSize: '13px',
      // uppercase-заголовок группы.
      groupTextColor: p.text3,
    },
    Card: {
      borderRadius: `${radii.card}px`,
      borderColor: p.border,
      titleFontSizeSmall: '14px',
      titleFontSizeMedium: '15px',
      paddingMedium: '16px',
    },
    Button: {
      borderRadiusMedium: `${radii.control}px`,
      borderRadiusSmall: `${radii.control}px`,
      heightSmall: '30px',
      heightMedium: '36px',
      fontWeight: '550',
    },
    Tag: {
      // Pill-чипы статусов — высота 21, скругление 11.
      borderRadius: '11px',
      heightSmall: '21px',
      fontSizeSmall: '11.5px',
      fontWeightStrong: '600',
    },
    Input: {
      borderRadius: `${radii.control}px`,
      heightMedium: '36px',
      heightSmall: '34px',
    },
    Dropdown: {
      borderRadius: `${radii.card}px`,
      padding: '4px',
      optionHeightMedium: '34px',
    },
    Drawer: {
      borderRadius: '0',
    },
    Modal: {
      borderRadius: `${radii.modal}px`,
    },
    DataTable: {
      borderRadius: `${radii.card}px`,
      borderColor: p.border,
      thColor: p.surface2,
      thTextColor: p.text3,
      thFontWeight: '600',
      thPaddingMedium: '9px 14px',
      tdPaddingMedium: '11px 14px',
      fontSizeMedium: '13px',
      tdColorHover: p.surface2,
    },
    Avatar: {
      borderRadius: '50%',
    },
    Divider: {
      color: p.border,
    },
    Breadcrumb: {
      fontSize: '13px',
      itemTextColor: p.text2,
      itemTextColorHover: p.text,
      separatorColor: p.text3,
    },
    Badge: {
      fontSize: '10.5px',
    },
    Tooltip: {
      borderRadius: `${radii.control}px`,
      padding: '4px 9px',
      textColor: p.surface,
      color: p.text,
    },
  }
}
